import { CustomerServiceTag, QuoteApiModel, QuoteOptionApiModel } from '~/lib/api/smartsend/types';
import { generateProtectionKey } from '../generateProtectionKey';
import { QuoteCard } from './card/QuoteCard';
import { IncludeService } from './card/IncludeService';
import { P2GText } from '@parcel2go/core-ui';
import ShowInView from '../../ShowInView';
import QuoteResultPlaceholder from './QuoteResultPlaceholder';
import { QuoteCardList } from './card/QuoteCardList';
import { QuoteTagType } from './utils/QuoteTagType';
import { CurrentSelectionType } from './utils/CurrentSelectionType';
import { QuoteLayoutVariant } from './layout/QuoteLayoutVariant';
import { QuoteCardSingleCourier } from './card/QuoteCardSingleCourier';
import { useCallback } from 'react';

export interface QuoteCardProps {
  variant: QuoteLayoutVariant;
  quote: QuoteApiModel;
  estimatedValue?: number;
  onSave: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  currentSelection: CurrentSelectionType;
  serviceTags: string[];
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  editMode: QuoteTagType;
  index: number;
  toggleSpecificTag: (tag: CustomerServiceTag) => void;
  maxQuoteOptions: number;
  isXs: boolean;
}

export function QuoteResult(props: QuoteCardProps) {
  const isSelected = useCallback(
    (o: QuoteOptionApiModel) =>
      props.currentSelection?.id === props.quote?.Service?.Id &&
      generateProtectionKey(
        props.currentSelection?.extras.map((e) => {
          return { Id: e };
        })
      ) === generateProtectionKey(o.Extras),
    [props.currentSelection, props.quote]
  );

  if (!props.quote || !props.quote.Service || !props.quote.Options) {
    return null;
  }

  if (props.editMode === 'Unfiltered') {
    return (
      <IncludeService quote={props.quote} serviceTags={props.serviceTags} updateServiceTags={props.updateServiceTags}>
        <P2GText variant="h6" colour="textSecondary">
          {props.quote.Service.Name}
        </P2GText>
      </IncludeService>
    );
  }

  switch (props.variant) {
    case 'OBA':
      return (
        <QuoteCardList
          toggleSpecificTag={props.toggleSpecificTag}
          quote={props.quote}
          onSave={props.onSave}
          key={props.quote.Service.Id}
          serviceTags={props.serviceTags}
          updateServiceTags={props.updateServiceTags}
          currentSelection={props.currentSelection}
          maxQuoteOptions={props.maxQuoteOptions}
          isXs={props.isXs}
        />
      );

    case 'MULTI-COURIER':
      return (
        <ShowInView placeholder={<QuoteResultPlaceholder />} override={props.index < 5}>
          <QuoteCard
            currentSelection={props.currentSelection}
            toggleSpecificTag={props.toggleSpecificTag}
            isSelected={isSelected}
            quote={props.quote}
            onSave={props.onSave}
            key={props.quote.Service.Id}
            editMode={props.editMode}
            serviceTags={props.serviceTags}
            updateServiceTags={props.updateServiceTags}
            maxQuoteOptions={props.maxQuoteOptions}
          />
        </ShowInView>
      );

    default:
      return (
        <ShowInView placeholder={<QuoteResultPlaceholder />} override={props.index < 5}>
          <QuoteCardSingleCourier
            currentSelection={props.currentSelection}
            toggleSpecificTag={props.toggleSpecificTag}
            isSelected={isSelected}
            quote={props.quote}
            onSave={props.onSave}
            key={props.quote.Service.Id}
            editMode={props.editMode}
            serviceTags={props.serviceTags}
            updateServiceTags={props.updateServiceTags}
            maxQuoteOptions={props.maxQuoteOptions}
            isXs={props.isXs}
          />
        </ShowInView>
      );
  }
}
